import { BannerRepository } from '../../domain/repositories/banner.repository';
import { BannerEntity } from '../../domain/entities/banner.entity';
import { BannerSettingsEntity } from '../../domain/entities/banner-settings.entity';
import { IHttpClient } from '../../application/interfaces/http-client.interface';
import { resolveMediaUrl } from '../utils/media-url.helper';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class HttpBannerRepository implements BannerRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  private mapHomeSlideToBanner(item: any): BannerEntity {
    const rawImageUrl = item.image?.url || item.imageUrl || '';
    const imageUrl = rawImageUrl ? resolveMediaUrl(rawImageUrl) : '';
    
    let subtitle = item.description || '';
    let mobileMediaUrl: string | undefined = undefined;
    let displayType: 'graphic' | 'editorial' | 'auto' = 'auto';
    let imageFit: 'cover' | 'contain' = 'cover';

    if (item.description && typeof item.description === 'string' && item.description.startsWith('{')) {
      try {
        const meta = JSON.parse(item.description);
        subtitle = meta.subtitle ?? meta.description ?? '';
        mobileMediaUrl = meta.mobileMediaUrl ? resolveMediaUrl(meta.mobileMediaUrl) : undefined;
        displayType = meta.displayType || 'auto';
        imageFit = meta.imageFit || 'cover';
      } catch {
        // En caso de que no sea JSON válido, se conserva como string normal
      }
    }

    const hasText = Boolean(
      item.title ||
      subtitle ||
      item.buttonTextPrimary ||
      item.buttonTextSecondary
    );
    return new BannerEntity({
      id: item.id,
      title: item.title || '',
      subtitle: subtitle,
      badgeText: item.badgeText || undefined,
      hasTextOverlay: displayType === 'graphic' ? false : (displayType === 'editorial' ? true : hasText),
      displayType,
      mediaType: 'image',
      mediaUrl: imageUrl,
      mobileMediaUrl: mobileMediaUrl,
      imageFit: imageFit,
      ctaText: item.buttonTextPrimary || undefined,
      ctaLink: item.buttonUrlPrimary || undefined,
      secondaryCtaText: item.buttonTextSecondary || undefined,
      secondaryCtaLink: item.buttonUrlSecondary || undefined,
      order: item.displayOrder ?? 0,
      isActive: item.isActive ?? true,
    });
  }

  public async getBanners(onlyActive: boolean = false): Promise<BannerEntity[]> {
    try {
      const endpoint = onlyActive ? '/home-slides/public' : '/home-slides';
      const res = await this.httpClient.get<ApiResponse<any[]>>(endpoint);
      if (!res.success || !res.data) {
        return [];
      }
      return res.data.map((item) => this.mapHomeSlideToBanner(item));
    } catch {
      try {
        const resPublic = await this.httpClient.get<ApiResponse<any[]>>('/home-slides/public');
        if (resPublic?.success && resPublic.data) {
          return resPublic.data.map((item) => this.mapHomeSlideToBanner(item));
        }
      } catch {
        // Silencio en catch secundario
      }
      return [];
    }
  }

  public async getBannerById(id: string): Promise<BannerEntity | null> {
    try {
      const res = await this.httpClient.get<ApiResponse<any>>(`/home-slides/${encodeURIComponent(id)}`);
      if (res?.success && res.data) {
        return this.mapHomeSlideToBanner(res.data);
      }
    } catch {
      // Silencio si no se encuentra
    }
    const banners = await this.getBanners(false);
    return banners.find((b) => b.id === id) || null;
  }

  public async saveBanner(banner: BannerEntity): Promise<BannerEntity> {
    const isNew = !banner.id || banner.id.startsWith('banner-');

    // Empaquetamos metadatos adicionales en description para compatibilidad
    let descriptionValue: string | null = null;
    if (banner.mobileMediaUrl || banner.displayType !== 'auto' || banner.imageFit !== 'cover') {
      descriptionValue = JSON.stringify({
        subtitle: banner.subtitle?.trim() || '',
        mobileMediaUrl: banner.mobileMediaUrl?.trim() || '',
        displayType: banner.displayType || 'auto',
        imageFit: banner.imageFit || 'cover',
      });
    } else {
      descriptionValue = banner.subtitle?.trim() || null;
    }

    const payload = {
      title: banner.title?.trim() || 'Banner Principal',
      description: descriptionValue,
      buttonTextPrimary: banner.ctaText?.trim() || null,
      buttonUrlPrimary: banner.ctaLink?.trim() || null,
      buttonTextSecondary: banner.secondaryCtaText?.trim() || null,
      buttonUrlSecondary: banner.secondaryCtaLink?.trim() || null,
      imageUrl: banner.mediaUrl?.trim() || null,
      displayOrder: Number(banner.order) || 0,
      isActive: banner.isActive ?? true,
    };

    if (isNew) {
      const res = await this.httpClient.post<ApiResponse<any>>('/home-slides', payload);
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Error al guardar el banner');
      }
      return this.mapHomeSlideToBanner(res.data);
    } else {
      const res = await this.httpClient.put<ApiResponse<any>>(
        `/home-slides/${encodeURIComponent(banner.id)}`,
        payload
      );
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Error al actualizar el banner');
      }
      return this.mapHomeSlideToBanner(res.data);
    }
  }

  public async toggleBannerStatus(id: string): Promise<BannerEntity> {
    const current = await this.getBannerById(id);
    if (!current) {
      throw new Error('Banner no encontrado');
    }
    const newStatus = !current.isActive;
    const res = await this.httpClient.patch<ApiResponse<any>>(
      `/home-slides/${encodeURIComponent(id)}/status`,
      { isActive: newStatus }
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al cambiar estado del banner');
    }
    return this.mapHomeSlideToBanner(res.data);
  }

  public async deleteBanner(id: string): Promise<void> {
    await this.httpClient.delete(`/home-slides/${encodeURIComponent(id)}`);
  }

  public async getSettings(): Promise<BannerSettingsEntity> {
    try {
      const res = await this.httpClient.get<ApiResponse<any>>('/home-slides/settings');
      if (res?.success && res.data) {
        const entity = new BannerSettingsEntity({
          layout: res.data.layout,
          size: res.data.size,
          autoplaySpeed: res.data.autoplaySpeed,
        });
        localStorage.setItem('bartech_banner_settings', JSON.stringify(entity));
        return entity;
      }
    } catch {
      // Fallback a localStorage si la API falla o está offline
      const cached = localStorage.getItem('bartech_banner_settings');
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          return new BannerSettingsEntity(parsed);
        } catch {
          // Ignorar error de parseo
        }
      }
    }
    return new BannerSettingsEntity();
  }

  public async saveSettings(settings: BannerSettingsEntity): Promise<BannerSettingsEntity> {
    const payload = {
      layout: settings.layout,
      size: settings.size,
      autoplaySpeed: settings.autoplaySpeed,
    };
    localStorage.setItem('bartech_banner_settings', JSON.stringify(payload));

    const res = await this.httpClient.put<ApiResponse<any>>('/home-slides/settings', payload);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al guardar la configuración de banners');
    }

    return new BannerSettingsEntity({
      layout: res.data.layout,
      size: res.data.size,
      autoplaySpeed: res.data.autoplaySpeed,
    });
  }
}
